import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { RoutePath } from 'common/constant/route-path';
import { UserPermissions } from 'common/constant/UserPermissions';
import { Logger } from 'common/utility/logger/logger';
import { ChangePasswordComponent } from 'components/change-password/change-password.component';
import { Size } from 'ngx-spinner';
import { Subject, takeUntil } from 'rxjs';
import { AuthenticationState } from 'services/authentication/authentication.service';
import { LoginGuardService } from 'services/authentication/login-guard/login-guard.service';
import { LoadingService } from 'services/loading/loading.service';
import { LoggingService } from 'services/logging/logging.service';
import { PreferredSettings, StorageService } from 'services/storage/storage.service';
import { TokenService } from 'services/token/token.service';
import { UserService } from 'services/user/user.service';
import { Currency } from './common/class/currency';
import { MenuItem } from './common/interface/MenuItem';
import { MenuNavigationItem } from './common/interface/MenuNavigationItems';
import { AuthenticationService } from './services/authentication/authentication.service';
import { TranslationService } from './services/translation/translation.service';

const Preferences: string = "Preferences"

const MainMenu: Array<MenuNavigationItem> = [
  createMenuItem(RoutePath.Dashboard),
  createMenuItem(RoutePath.Analyze, 'query_stats'),
  createMenuItem(RoutePath.Administration, 'admin_panel_settings'),
  createMenuItem(RoutePath.EdgeDevice, 'settings_input_composite'),
  createMenuItem(RoutePath.User, 'manage_accounts'),
  createMenuItem(RoutePath.Report, 'area_chart'),
  createMenuItem(RoutePath.Visual, 'ssid_chart'),
  createMenuItem(RoutePath.UserReport, 'leaderboard')
]

function createMenuItem(labelKey: string, iconName?: string, route?: string): any {
  route = route ?? labelKey
  iconName = iconName ?? labelKey
  return {
    label: `navigation.${labelKey}`,
    icon: iconName,
    router: route
  }
}


const ProfileMenu: MenuItem[] = [
  { id: 1, label: 'navigation.logout', icon: "logout" },
  { id: 2, label: 'navigation.password', icon: "fingerprint" },
  // TODO: When we add preferences feature
  //    { id: 3, label: 'navigation.settings', icon: 'settings' }
]

@Component({
  selector: 'ems-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  private unsubscribe: Subject<void> = new Subject();
  showMainMenuLabels: boolean = true;
  selectedLanguage: string = 'en';

  public currentCurrency: string = Currency.Default
  public preferencesDrawerEnabled: boolean = false;

  public get Preferences(): string {
    return Preferences;
  }

  public get CurrencyLabel(): string {
    return Currency.DefaultLabel;
  }

  public get CurrencyFormats(): Array<MenuItem> {
    return Currency.Formats;
  }

  public get MainMenu(): Array<MenuNavigationItem> {
    return MainMenu;
  }

  public get ProfileMenu(): Array<MenuItem> {
    return ProfileMenu;
  }

  public get Authenticated() {
    const returnValue: boolean = this.authenticationService.loggedIn;
    this.checkToken()
    return returnValue
  }

  public get canMaintainUsers(): boolean {
    return this.userService.hasPermission(UserPermissions.MaintainUsers)
  }

  public canShowMenuItem(route: string): boolean {
    let returnValue: boolean = false
    switch (route) {
      case RoutePath.User:
      case RoutePath.Administration:
      case RoutePath.EdgeDevice:
        returnValue = this.canMaintainUsers
        break;

      case RoutePath.UserReport:
      case RoutePath.Visual:
      case RoutePath.Dashboard:
      case RoutePath.Analyze:
        returnValue = false
        break;

      case RoutePath.Report:
        returnValue = true
        break;

    }
    return returnValue
  }

  constructor(
    private translationService: TranslationService,
    private authenticationService: AuthenticationService,
    private loadingService: LoadingService,
    private userService: UserService,
    private dialog: MatDialog,
    private router: Router,
    private tokenService: TokenService,
    private loginGuardService: LoginGuardService,
    private loggingService: LoggingService
  ) {
    Logger.LoggingService = this.loggingService
    this.listenForAuthenticationState();
  }

  ngOnInit() {
    this.translationService.setCurrentLanguage();
    window.dispatchEvent(new Event('resize'));
  } // ngOnInit()

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  } // ngOnDestroy()


  private listenForAuthenticationState(): void {
    this.authenticationService.listenForAuthenticationState.
      pipe(takeUntil(this.unsubscribe)).subscribe((authState) => {
        if (authState === AuthenticationState.Authenticated) {
          if (StorageService.getUserPreferredSettings(PreferredSettings.MainMenuShowLabels) === undefined) {
            this.showMainMenuLabels = true;
            StorageService.setUserPreferredSettings(PreferredSettings.MainMenuShowLabels, this.showMainMenuLabels);
          } else {
            this.showMainMenuLabels = JSON.parse(StorageService.getUserPreferredSettings(PreferredSettings.MainMenuShowLabels));
          }
        }
      });
  } // listenForAuthenticationState()


  public togglePreferences() {
    this.preferencesDrawerEnabled = !this.preferencesDrawerEnabled;
    return this.preferencesDrawerEnabled;
  }

  public clickProfileMenu(menu: MenuItem) {
    switch (menu.id) {
      case 1: // Logout
        this.authenticationService.logout()
        break
      case 2: // Password
        this.changePasswordDialog()

        // TODO Change Password Dialog
        break;

      // TODO when we add preference feature
      // case 3: // Settings Tray
      //   this.togglePreferences();
      //   break;
      default:
        break;
    }
  }

  public showText() {
    this.showMainMenuLabels = !this.showMainMenuLabels;
    StorageService.setUserPreferredSettings(PreferredSettings.MainMenuShowLabels, this.showMainMenuLabels);
    window.dispatchEvent(new Event('resize'));
  }

  public get spinnerType(): string {
    return this.loadingService.spinnerType;
  }
  public get spinnerSize(): Size {
    return this.loadingService.spinnerSize;
  }

  private changePasswordDialog(): void {
    this.dialog.open(ChangePasswordComponent, { disableClose: true });
  }

  private checkToken(): void {
    if (this.loginGuardService.secureRoute() && !this.tokenService.valid){
      this.router.navigate([RoutePath.Login])
    }
  }
}
